export class OrderedActionProcessor<T> {
  /**
   * @param listener Will be called with the result of an action.
   * The listener will not be called if a later action has already
   * completed
   *
   * @param errorHandler If an action throws an exception then this
   * errorHandler will be called (with the error as an argument). The
   * errorHandle should return true if the action should be retried,
   * or false if it should be aborted
   */
  constructor(listener: (payload: T) => void, errorHandler: (err: any) => boolean) {
    this.impl = new KeyedOrderedActionProcessor<number, T>(
      (_key: number, payload: T) => listener(payload),
      errorHandler
    );
  }

  enqueue(action: () => Promise<T>): void {
    const KEY = 0;
    this.impl.enqueue(KEY, action);
  }

  /**
   * shutdown everything. Any in-flight actions will be discarded
   * (their results will be ignored, and they will never be retried)
   *
   * This can only be called once.
   */
  public close(): void {
    this.impl.close();
  }

  private readonly impl: KeyedOrderedActionProcessor<number, T>;
}

/**
 * Runs an asynchronous action, and reports on the result (but only if a
 * newer request hasn't already completed)
 *
 * The Key type must be number or string
 */
export class KeyedOrderedActionProcessor<K, T> {
  /**
   * @param listener Will be called with the result of an action.
   * The listener will not be called if a later action has already
   * completed with the same key
   *
   * @param errorHandler If an action throws an exception then this
   * errorHandler will be called (with the error as an argument). The
   * errorHandle should return true if the action should be retried,
   * or false if it should be aborted
   */
  constructor(
    private listener: (key: K, payload: T) => void,
    private errorHandler: (err: any) => boolean
  ) {}

  enqueue(key: K, action: () => Promise<T>, errorRetrySleepTime: number = 500): void {
    const prev = this.lastResolvedActions.get(key);
    let actionSentCounter: number;
    if (prev === undefined) {
      this.lastResolvedActions.set(key, [1, -1]);
      actionSentCounter = 1;
    } else {
      actionSentCounter = prev[0] + 1;
      this.lastResolvedActions.set(key, [actionSentCounter, prev[1]]);
    }
    action().then(
      (result: T) => {
        if (this.closed) {
          return;
        }

        const last = this.lastResolvedActions.get(key);
        if (last === undefined) {
          throw new Error("The Impossible Happened");
        }
        if (actionSentCounter > last[1]) {
          this.lastResolvedActions.set(key, [last[0], actionSentCounter]);
          this.listener(key, result);
        }
      },
      (err: any) => {
        if (this.closed) {
          return;
        }

        const last = this.lastResolvedActions.get(key);
        if (last === undefined) {
          throw new Error("The Impossible Happened");
        }
        // No need to retry if we already received the result of a later action
        if (actionSentCounter > last[1]) {
          const retry = this.errorHandler(err);
          if (retry) {
            setTimeout(
              () => this.enqueue(key, action, increaseSleepTime(errorRetrySleepTime)),
              errorRetrySleepTime
            );
          }
        }
      }
    );
  }

  /**
   * shutdown everything. Any in-flight actions will be discarded
   * (their results will be ignored, and they will never be retried)
   *
   * This can only be called once.
   */
  public close(): void {
    if (this.closed) {
      throw new Error("Already closed");
    }
    this.closed = true;
  }

  private lastResolvedActions = new Map<K, [number, number]>();

  private closed = false;
}

function increaseSleepTime(oldSleep: number): number {
  const MULTIPLIER = 1.4;
  const MAX_SLEEP = 20000;
  const newSleep = oldSleep * MULTIPLIER;
  if (newSleep > MAX_SLEEP) {
    return MAX_SLEEP;
  } else {
    return newSleep;
  }
}
